import { Badge, Button, Drawer, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { cancelPt, changePtTrainer, completePt, noShowPt, reschedulePt, waivePt, type PtSession } from "../../api/pt";
import { useServices } from "../../app/services";
import { Confirm, Field, FormError, messageOf, styles as c, useScope } from "./common";
import { fromMinutes, toMinutes } from "./time";

export interface Prefill { date: string; start: string; end: string; trainerId: string }
const TONE = { booked: "info", completed: "ok", no_show: "crit", late_cancel: "crit", cancelled: "neutral", rescheduled: "neutral" } as const;
const LABEL: Record<string, string> = { booked: "Booked", completed: "Completed", no_show: "No-show", late_cancel: "Late cancel", cancelled: "Cancelled", rescheduled: "Rescheduled" };

export function PtSessionDrawer({ session, trainers, nameOf, prefill, onClose }: {
  session: PtSession | null; trainers: string[]; nameOf: (id: string | null, known?: string | null) => string; prefill: Prefill | null; onClose: () => void;
}) {
  const { client } = useServices();
  const sc = useScope();
  const qc = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"none" | "reschedule" | "trainer">("none");
  // One lever per action (D57), as each POST /pt/sessions/:id/* route checks; the override is its own lever.
  const can = { reschedule: sc.ctx.has("pt.reschedule"), trainer: sc.ctx.has("pt.change_trainer"), complete: sc.ctx.has("pt.complete"), cancel: sc.ctx.has("pt.cancel") };
  const canOverride = sc.ctx.has("pt.override");
  const [form, setForm] = useState<Prefill>({ date: "", start: "", end: "", trainerId: "" });
  const [override, setOverride] = useState(false);
  const [scope, setScope] = useState<"session" | "rest_of_pack">("session");
  const [newTrainer, setNewTrainer] = useState("");

  useEffect(() => {
    if (!session) return;
    setError(null); setOverride(false); setScope("session"); setNewTrainer(session.trainerId);
    setForm(prefill ?? { date: session.date, start: session.start, end: session.end, trainerId: session.trainerId });
    setMode(prefill ? "reschedule" : "none");
  }, [session?.id, prefill?.date, prefill?.start, prefill?.trainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pt"] }); void qc.invalidateQueries({ queryKey: ["classes"] }); },
    onError: (e) => setError(messageOf(e)),
  });
  const go = (fn: () => Promise<unknown>, done: string) => run.mutate(fn, { onSuccess: () => { toast.show(done); onClose(); } });
  const s = session;
  const open = s !== null;
  const duration = s ? toMinutes(s.end) - toMinutes(s.start) : 60;
  const setStart = (start: string) => setForm((f) => ({ ...f, start, end: /^\d\d:\d\d$/.test(start) ? fromMinutes(Math.min(1439, toMinutes(start) + duration)) : f.end }));

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()} title={s?.member?.name ?? "PT session"} description={s ? `${formatDate(s.date, sc.ctx.locale, "long")} · ${s.start}–${s.end} · ${nameOf(s.trainerId, s.trainerName)}` : undefined}>
      {s ? (
        <div className={`${c.stack} ${c.drawerPad}`}>
          <div className={c.row}>
            <Badge tone={TONE[s.status]}>{LABEL[s.status] ?? s.status}</Badge>
            {s.sessionIndex ? <Badge>Session {s.sessionIndex}</Badge> : null}
            {s.rescheduleCount > 0 ? <Badge tone="warn">Moved {s.rescheduleCount}×</Badge> : null}
            {s.member?.phone ? <span className={c.note}>{s.member.phone}</span> : null}
          </div>
          <FormError message={error} />
          {s.status === "booked" && (can.reschedule || can.trainer || can.complete || can.cancel) ? (
            <div className={c.row}>
              {can.reschedule ? <Button onClick={() => setMode(mode === "reschedule" ? "none" : "reschedule")}>Reschedule</Button> : null}
              {can.trainer ? <Button onClick={() => setMode(mode === "trainer" ? "none" : "trainer")}>Change trainer</Button> : null}
              {can.complete ? <Button busy={run.isPending} busyLabel="Working…" onClick={() => go(() => completePt(client, s.id), "Session completed")}>Mark complete</Button> : null}
              {can.complete ? <Button busy={run.isPending} busyLabel="Working…" onClick={() => go(() => noShowPt(client, s.id), "Marked no-show")}>No-show</Button> : null}
              {can.cancel ? <Confirm label="Cancel session" question="Cancel this session? A late cancel may use up a session." busy={run.isPending} onConfirm={() => go(() => cancelPt(client, s.id), "Session cancelled")} /> : null}
            </div>
          ) : null}
          {s.status === "no_show" || s.status === "late_cancel" ? (
            sc.ctx.has("pt.waive") ? <div><Button busy={run.isPending} busyLabel="Working…" onClick={() => go(() => waivePt(client, s.id), "Charge waived")}>Waive</Button></div> : <p className={c.note}>Waiving a {LABEL[s.status]!.toLowerCase()} needs more access than your role has.</p>
          ) : null}
          {mode === "reschedule" ? (
            <form className={c.stack} aria-label="Reschedule" onSubmit={(e) => { e.preventDefault(); go(() => reschedulePt(client, s.id, { date: form.date, start: form.start, end: form.end, ...(form.trainerId !== s.trainerId ? { trainerId: form.trainerId } : {}), ...(canOverride && override ? { override: true } : {}) }), "Session rescheduled"); }}>
              <Field label="Date" htmlFor="pt-date"><input id="pt-date" type="date" className={c.input} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              <div className={c.row}>
                <Field label="Starts" htmlFor="pt-start"><input id="pt-start" type="time" className={c.input} value={form.start} onChange={(e) => setStart(e.target.value)} /></Field>
                <Field label="Ends" htmlFor="pt-end"><input id="pt-end" type="time" className={c.input} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></Field>
              </div>
              <Field label="Trainer" htmlFor="pt-trainer">
                <select id="pt-trainer" className={c.input} value={form.trainerId} onChange={(e) => setForm({ ...form, trainerId: e.target.value })}>{trainers.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}</select>
              </Field>
              {canOverride ? <label className={c.check}><input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />Override notice and reschedule limits</label> : null}
              <div><Button variant="primary" type="submit" busy={run.isPending}>Move session</Button></div>
            </form>
          ) : null}
          {mode === "trainer" ? (
            <form className={c.stack} aria-label="Change trainer" onSubmit={(e) => { e.preventDefault(); go(() => changePtTrainer(client, s.id, { trainerId: newTrainer, scope }), "Trainer changed"); }}>
              <Field label="New trainer" htmlFor="pt-newtrainer">
                <select id="pt-newtrainer" className={c.input} value={newTrainer} onChange={(e) => setNewTrainer(e.target.value)}>{trainers.map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}</select>
              </Field>
              <Field label="Applies to" htmlFor="pt-scope">
                <select id="pt-scope" className={c.input} value={scope} onChange={(e) => setScope(e.target.value as "session" | "rest_of_pack")}><option value="session">This session only</option><option value="rest_of_pack">The rest of the pack</option></select>
              </Field>
              <div><Button variant="primary" type="submit" busy={run.isPending}>Change trainer</Button></div>
            </form>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
